import React from 'react'
import { AlertTriangle, Check, Clock, ShieldCheck } from 'lucide-react'
import { nilGoLabel, type NilCompliance } from '@adthlete/common/utils/nilCompliance'
import { cn } from '@/lib/utils'

const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''

/** Fixed colours, so they read the same under the app's flipped light palette */
const LOOK = {
  cleared: { icon: Check, text: 'text-[#3F7A0A]', pill: 'bg-[#E8F9D2] text-[#2F5E06]' },
  due: { icon: Clock, text: 'text-[#B45309]', pill: 'bg-[#FEF3D7] text-[#92400E]' },
  overdue: { icon: AlertTriangle, text: 'text-[#DC2626]', pill: 'bg-[#FEE2E2] text-[#B91C1C]' },
  waiting: { icon: ShieldCheck, text: 'text-[#6B7280]', pill: 'bg-[#F3F4F6] text-[#4B5563]' },
} as const

/** A small pill for deal cards */
export const NilGoBadge: React.FC<{ compliance: NilCompliance | undefined | null }> = ({
  compliance: c,
}) => {
  if (!c?.required || c.state === 'not_required') return null
  const look = LOOK[c.state]
  const Icon = look.icon
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
        look.pill,
      )}
      title="NIL Go clearance"
    >
      <Icon className="h-3 w-3" /> NIL Go · {nilGoLabel(c)}
    </span>
  )
}

/**
 * NIL Go clearance as a step in the deal's workflow. Deals of $600 or more must be cleared by the
 * NIL Go clearinghouse before content goes live; the athlete files within 5 business days of
 * signing. The brand can see where that stands without asking.
 */
export const NilGoLine: React.FC<{
  compliance: NilCompliance | undefined | null
  athleteName: string
}> = ({ compliance: c, athleteName }) => {
  if (!c?.required || c.state === 'not_required') return null
  const first = athleteName.split(' ')[0]
  const look = LOOK[c.state]
  const Icon = look.icon
  const detail =
    c.state === 'cleared'
      ? `Cleared${c.clearedAt ? ` ${shortDate(c.clearedAt)}` : ''}${
          c.confirmationNumber ? ` · Confirmation ${c.confirmationNumber}` : ''
        }. Content can go live.`
      : c.state === 'waiting'
        ? `${first} files with the NIL Go clearinghouse within 5 business days of signing.`
        : c.state === 'overdue'
          ? `${first} was due to file by ${shortDate(c.deadline)}. Content can't go live until it's cleared.`
          : `${first} files by ${shortDate(c.deadline)}${
              c.inProgress ? ' (started)' : ''
            }. Content can't go live until it's cleared.`
  return (
    <div className="flex items-start gap-3 border-b border-foreground/10 pb-3">
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', look.text)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground">NIL Go clearance</span>
          <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', look.pill)}>
            {nilGoLabel(c)}
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">{detail}</p>
        {c.state === 'cleared' || c.state === 'due' || c.state === 'overdue' ? (
          <p className="mt-0.5 text-xs text-gray-500">
            School notified: {c.schoolReported ? 'yes' : 'not yet'}
          </p>
        ) : null}
      </div>
    </div>
  )
}
